/**
 * GerstnerWaves — pure trochoidal-wave math shared by the realistic water
 * material (TSL vertex/fragment stages rebuild these exact formulas from the
 * same wave array) and CPU consumers (`WaterSystem.getWaterHeightAt`,
 * buoyancy). One parameter set, two evaluators — visuals and physics can't
 * drift apart.
 *
 * Implementation follows GPU Gems ch. 1, "Effective Water Simulation from
 * Physical Models" (Finch, NVIDIA 2004):
 *   https://developer.nvidia.com/gpugems/gpugems/part-i-natural-effects/chapter-1-effective-water-simulation-physical-models
 *  - deep-water dispersion  w = sqrt(g · 2π/L)
 *  - horizontal displacement Q·A·D·cos(w·D·p + φt), vertical A·sin(…)
 *  - per-wave steepness normalized Qᵢ = Q/(wᵢ·Aᵢ·N) so crests never loop
 *  - analytic normal N = (−ΣD.x·w·A·C, 1−ΣQ·w·A·S, −ΣD.z·w·A·C)
 *
 * No three.js import — unit-testable in plain Node.
 */

const GRAVITY = 9.81;
const TAU = Math.PI * 2;

export interface GerstnerWave {
  /** Normalized travel direction (XZ plane). */
  dirX: number;
  dirZ: number;
  /** Crest amplitude (world units). */
  amplitude: number;
  /** Crest-to-crest wavelength (world units). */
  wavelength: number;
  /** Normalized per-wave steepness Qᵢ (already divided by w·A·N). */
  q: number;
  /** Angular frequency w = sqrt(g·2π/L). */
  w: number;
  /** Phase-speed multiplier φ = speed · w. */
  phi: number;
}

/**
 * Runtime SEA-STATE modulation (2026-09-11, interactive water): the wind
 * scales the whole displacement field and turns its heading WITHOUT a
 * shader rebuild. The CPU samplers below and the TSL evaluators
 * (tsl-gerstner.ts) apply the SAME two numbers, so buoyancy/height queries
 * keep matching the mesh. Scaling amplitude with a fixed Qᵢ scales the
 * horizontal chop too (steepness ∝ ampScale): keep ampScale ≲ 1/steepness
 * or crests loop.
 */
export interface WaveModulation {
  /** Linear amplitude scale (1 = the authored set). */
  ampScale: number;
  /** Rotation of every wave heading about +Y, radians (+X turns toward +Z). */
  dirRot: number;
}

export const IDENTITY_MODULATION: Readonly<WaveModulation> = Object.freeze({ ampScale: 1, dirRot: 0 });

export interface GerstnerWaveSetConfig {
  /** Master amplitude of the largest wave (world units). Default 0.25. */
  amplitude?: number;
  /** Wavelength of the largest wave (world units). Default 14. */
  wavelength?: number;
  /** Master steepness 0..1 (1 = sharpest non-looping crests). Default 0.55. */
  steepness?: number;
  /** Wave-speed multiplier (1 = physical deep-water speed). Default 1. */
  speed?: number;
  /** Number of summed waves, 1..8. Default 4. */
  count?: number;
  /** Mean travel heading in degrees (0 = +X). Default 25. */
  directionDeg?: number;
  /** Direction spread around the mean, degrees. Default 55. */
  spreadDeg?: number;
  /** Deterministic seed — same seed, same sea. Default 1789. */
  seed?: number;
}

/** Deterministic LCG (reproducible seas; no Math.random). */
function lcg(state: number): number {
  return (state * 1664525 + 1013904223) >>> 0;
}

/**
 * Build a deterministic wave set: geometric wavelength/amplitude cascade
 * (each successive wave ~×0.62 shorter/lower — a plausible spectrum shape)
 * with directions fanned around the mean heading.
 */
export function createWaveSet(config: GerstnerWaveSetConfig = {}): GerstnerWave[] {
  const amplitude = config.amplitude ?? 0.25;
  const wavelength = config.wavelength ?? 14;
  const steepness = Math.min(Math.max(config.steepness ?? 0.55, 0), 1);
  const speed = config.speed ?? 1;
  const count = Math.min(Math.max(Math.floor(config.count ?? 4), 1), 8);
  const directionRad = ((config.directionDeg ?? 25) * Math.PI) / 180;
  const spreadRad = ((config.spreadDeg ?? 55) * Math.PI) / 180;
  let seed = (config.seed ?? 1789) >>> 0;

  const waves: GerstnerWave[] = [];
  for (let i = 0; i < count; i++) {
    const falloff = Math.pow(0.62, i);
    seed = lcg(seed);
    const dirJitter = (seed / 0xffffffff - 0.5) * spreadRad;
    const heading = directionRad + dirJitter;
    const L = Math.max(wavelength * falloff, 0.05);
    const A = amplitude * falloff;
    // `w` is the SPATIAL wavenumber k = 2π/L (GPU Gems eq. 8-9); the temporal
    // rate is the deep-water dispersion ω = √(g·k). The old code stored ω in
    // `w` and `speed·ω` in `phi`: every crest spacing came out as 2π/ω (a
    // 14 m wave rendered ~3 m apart) and every component travelled at
    // `speed` m/s — no dispersion at all.
    const w = TAU / L;
    const omega = Math.sqrt(GRAVITY * w);
    waves.push({
      dirX: Math.cos(heading),
      dirZ: Math.sin(heading),
      amplitude: A,
      wavelength: L,
      // Normalized so the SUM of waves can't loop crests (GPU Gems eq. 8).
      q: A > 1e-9 ? steepness / (w * A * count) : 0,
      w,
      phi: speed * omega,
    });
  }
  return waves;
}

/** Total crest amplitude — foam/crest thresholds scale from this. */
export function maxAmplitude(waves: GerstnerWave[]): number {
  return waves.reduce((sum, wv) => sum + wv.amplitude, 0);
}

/**
 * Waves the DISPLACEMENT grid can actually represent. A vertex grid with
 * spacing `s` folds every wavelength below its Nyquist limit 2·s into
 * pseudo-random per-vertex phase samples — each quad gets an independent
 * tilt and the sheet reads as a checkerboard of flat tiles instead of a
 * sea (the classic aliasing artifact). The standard split: geometry
 * carries only wavelengths ≥ `minWavelength` (pick ~2.5× the grid step);
 * everything shorter stays in the per-pixel analytic normal / detail
 * maps, which evaluate per fragment and cannot alias on the mesh.
 * `minWavelength <= 0` disables filtering (returns the input array).
 */
export function filterVertexWaves(waves: GerstnerWave[], minWavelength: number): GerstnerWave[] {
  if (minWavelength <= 0) return waves;
  return waves.filter((wv) => wv.wavelength >= minWavelength);
}

/**
 * Surface displacement of the grid point that STARTED at (x, z):
 * horizontal Q·A·D·cos + vertical A·sin (GPU Gems eq. 9).
 */
export function sampleDisplacement(
  waves: GerstnerWave[],
  x: number,
  z: number,
  t: number,
  mod: Readonly<WaveModulation> = IDENTITY_MODULATION,
): { dx: number; dy: number; dz: number } {
  let dx = 0;
  let dy = 0;
  let dz = 0;
  const rc = Math.cos(mod.dirRot);
  const rs = Math.sin(mod.dirRot);
  for (const wv of waves) {
    const dX = wv.dirX * rc - wv.dirZ * rs;
    const dZ = wv.dirX * rs + wv.dirZ * rc;
    const amp = wv.amplitude * mod.ampScale;
    const phase = wv.w * (dX * x + dZ * z) + wv.phi * t;
    const c = Math.cos(phase);
    dy += amp * Math.sin(phase);
    dx += wv.q * amp * dX * c;
    dz += wv.q * amp * dZ * c;
  }
  return { dx, dy, dz };
}

/**
 * Water HEIGHT above (x, z) — what buoyancy asks. Gerstner displaces grid
 * points horizontally, so the surface above a fixed world column is the
 * displacement of some *other* grid point; two fixed-point iterations of
 * p ← target − horizontalDisp(p) recover it to sub-centimeter accuracy for
 * sane steepness (standard inversion; see the GPU Gems chapter's discussion
 * of trochoid geometry).
 */
export function sampleHeight(
  waves: GerstnerWave[],
  x: number,
  z: number,
  t: number,
  mod: Readonly<WaveModulation> = IDENTITY_MODULATION,
): number {
  let px = x;
  let pz = z;
  for (let i = 0; i < 2; i++) {
    const d = sampleDisplacement(waves, px, pz, t, mod);
    px = x - d.dx;
    pz = z - d.dz;
  }
  return sampleDisplacement(waves, px, pz, t, mod).dy;
}

/**
 * Jacobian determinant of the horizontal Gerstner map at grid point (x, z):
 * J = Jxx·Jzz − Jxz². Calm water sits at J ≈ 1; crests compress the surface
 * (J falls) and FOLD where J ≤ 0 — the standard whitecap criterion
 * (Tessendorf 2001 §4.5 "choppy waves" folding measure).
 */
export function sampleJacobian(
  waves: GerstnerWave[],
  x: number,
  z: number,
  t: number,
  mod: Readonly<WaveModulation> = IDENTITY_MODULATION,
): number {
  let jxx = 1;
  let jzz = 1;
  let jxz = 0;
  const rc = Math.cos(mod.dirRot);
  const rs = Math.sin(mod.dirRot);
  for (const wv of waves) {
    const dX = wv.dirX * rc - wv.dirZ * rs;
    const dZ = wv.dirX * rs + wv.dirZ * rc;
    const phase = wv.w * (dX * x + dZ * z) + wv.phi * t;
    const qaw = wv.q * wv.amplitude * mod.ampScale * wv.w * Math.sin(phase);
    jxx -= qaw * dX * dX;
    jzz -= qaw * dZ * dZ;
    jxz -= qaw * dX * dZ;
  }
  return jxx * jzz - jxz * jxz;
}

/** Analytic surface normal at grid point (x, z) (GPU Gems eq. 10–12). */
export function sampleNormal(
  waves: GerstnerWave[],
  x: number,
  z: number,
  t: number,
  mod: Readonly<WaveModulation> = IDENTITY_MODULATION,
): { nx: number; ny: number; nz: number } {
  let nx = 0;
  let ny = 1;
  let nz = 0;
  const rc = Math.cos(mod.dirRot);
  const rs = Math.sin(mod.dirRot);
  for (const wv of waves) {
    const dX = wv.dirX * rc - wv.dirZ * rs;
    const dZ = wv.dirX * rs + wv.dirZ * rc;
    const phase = wv.w * (dX * x + dZ * z) + wv.phi * t;
    const wa = wv.w * wv.amplitude * mod.ampScale;
    const c = Math.cos(phase);
    const s = Math.sin(phase);
    nx -= dX * wa * c;
    nz -= dZ * wa * c;
    ny -= wv.q * wa * s;
  }
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
  return { nx: nx / len, ny: ny / len, nz: nz / len };
}

/**
 * Scan `samples` seeded random points for the most compressed (lowest-J)
 * crest — where spray should erupt. Deterministic per (seed, time bucket).
 */
export function findCrestPoint(
  waves: GerstnerWave[],
  sizeX: number,
  sizeZ: number,
  t: number,
  samples = 24,
  seed = 977,
): { x: number; z: number; jacobian: number } {
  let s = (seed ^ (Math.floor(t * 10) * 2654435761)) >>> 0;
  let best = { x: 0, z: 0, jacobian: Infinity };
  for (let i = 0; i < samples; i++) {
    s = lcg(s);
    const x = (s / 0xffffffff - 0.5) * sizeX;
    s = lcg(s);
    const z = (s / 0xffffffff - 0.5) * sizeZ;
    const j = sampleJacobian(waves, x, z, t);
    if (j < best.jacobian) best = { x, z, jacobian: j };
  }
  return best;
}

// ─── Phillips-spectrum sampling (Tessendorf 2001) ────────────────────────────

export interface SpectrumWaveSetConfig {
  /** Wind speed in m/s — sets the sea state (peak wavelength ∝ V²). Default 8. */
  windSpeed?: number;
  /** Wind heading in degrees (0 = +X). Default 25. */
  windDirectionDeg?: number;
  /** Number of sampled components, 1..16. Default 8. */
  count?: number;
  /** Master steepness 0..1 (Gerstner crest sharpness). Default 0.6. */
  steepness?: number;
  /** Linear scale on physical amplitudes (artistic control). Default 1. */
  amplitudeScale?: number;
  /** Deterministic seed. Default 1789. */
  seed?: number;
  gravity?: number;
  /**
   * Spectrum family. 'phillips' (default — Tessendorf's original),
   * 'pierson-moskowitz' (fully developed sea, wind only) or 'jonswap'
   * (fetch-limited, peaked — the modern standard).
   */
  model?: 'phillips' | 'pierson-moskowitz' | 'jonswap';
  /** JONSWAP fetch in kilometres — how far the wind has blown. Default 100. */
  fetchKm?: number;
  /** JONSWAP peak-enhancement γ. Default 3.3. */
  gamma?: number;
  /**
   * Water depth in metres. Finite depth switches dispersion to
   * ω² = gk·tanh(kD) (waves slow and shorten) and applies the
   * Kitaigorodskii/TMA depth attenuation. Default: deep water.
   */
  depth?: number;
  /**
   * Secondary swell system (its own wind speed/heading/model) merged into
   * the set — long swell from a distant storm under local wind chop.
   * Steepness is re-normalized across the COMBINED set so crests never loop.
   */
  swell?: Omit<SpectrumWaveSetConfig, 'swell'>;
}

/**
 * Sample the Phillips spectrum into a Gerstner wave set — the middle ground
 * between the artist cascade above and a full FFT ocean: wave lengths,
 * amplitudes and directions come from oceanographic statistics
 * (Tessendorf, "Simulating Ocean Water", SIGGRAPH 2001 course):
 *
 *   P(k) = A · exp(−1/(kL)²)/k⁴ · |k̂·ŵ|²,  L = V²/g
 *
 * with the standard small-wave cutoff exp(−k²l²), l = L/1000. Each log-spaced
 * k-band contributes one component with amplitude √(2·P(k)·Δk) (a sinusoid of
 * amplitude a carries variance a²/2), so doubling the wind speed genuinely
 * changes the sea state instead of just scaling heights.
 */
export function createWaveSetFromSpectrum(config: SpectrumWaveSetConfig = {}): GerstnerWave[] {
  const primary = sampleSpectrum(config);
  if (!config.swell) return primary;

  // Merge a secondary swell system, then RE-normalize steepness across the
  // combined set — Q is divided by wave count precisely so the summed
  // horizontal displacement can't loop crests (GPU Gems eq. 8).
  const swell = sampleSpectrum({ count: 4, seed: (config.seed ?? 1789) + 7919, ...config.swell });
  const all = [...primary, ...swell];
  const steepness = Math.min(Math.max(config.steepness ?? 0.6, 0), 1);
  for (const wv of all) {
    wv.q = wv.amplitude > 1e-9 ? steepness / (wv.w * wv.amplitude * all.length) : 0;
  }
  return all;
}

/** One spectrum family sampled into Gerstner components (no swell mixing). */
function sampleSpectrum(config: Omit<SpectrumWaveSetConfig, 'swell'>): GerstnerWave[] {
  const g = config.gravity ?? GRAVITY;
  const V = Math.max(config.windSpeed ?? 8, 0.5);
  const windRad = ((config.windDirectionDeg ?? 25) * Math.PI) / 180;
  const count = Math.min(Math.max(Math.floor(config.count ?? 8), 1), 16);
  const steepness = Math.min(Math.max(config.steepness ?? 0.6, 0), 1);
  const ampScale = config.amplitudeScale ?? 1;
  const model = config.model ?? 'phillips';
  const depth = config.depth ?? Infinity;
  let seed = (config.seed ?? 1789) >>> 0;
  const rand = (): number => {
    seed = lcg(seed);
    return seed / 0xffffffff;
  };

  // Finite-depth dispersion ω² = gk·tanh(kD); deep water: tanh → 1.
  const omegaOf = (k: number): number =>
    Number.isFinite(depth) ? Math.sqrt(g * k * Math.tanh(k * depth)) : Math.sqrt(g * k);
  // dω/dk — the S(ω) → S(k) change-of-variable Jacobian.
  const dOmegaDk = (k: number): number => {
    const w = omegaOf(k);
    if (!Number.isFinite(depth)) return g / (2 * w);
    const th = Math.tanh(k * depth);
    const sech2 = 1 - th * th;
    return (g * (th + k * depth * sech2)) / (2 * w);
  };
  // Kitaigorodskii/TMA depth attenuation φ(ω√(D/g)) — shallow water carries
  // less energy at low frequencies (piecewise approximation).
  const tma = (w: number): number => {
    if (!Number.isFinite(depth)) return 1;
    const wh = w * Math.sqrt(depth / g);
    if (wh <= 1) return 0.5 * wh * wh;
    if (wh < 2) return 1 - 0.5 * (2 - wh) * (2 - wh);
    return 1;
  };

  // ── Spectrum S(ω) per family + its peak frequency ─────────────────────────
  const ALPHA_PM = 0.0081;
  let sOmega: (w: number) => number;
  let omegaPeak: number;
  if (model === 'jonswap') {
    const fetch = Math.max(1, config.fetchKm ?? 100) * 1000;
    const gamma = config.gamma ?? 3.3;
    // Hasselmann 1973 fetch relations.
    const alpha = 0.076 * Math.pow((V * V) / (fetch * g), 0.22);
    omegaPeak = 22 * Math.pow((g * g) / (V * fetch), 1 / 3);
    sOmega = (w) => {
      const sigma = w <= omegaPeak ? 0.07 : 0.09;
      const r = Math.exp(-((w - omegaPeak) ** 2) / (2 * sigma * sigma * omegaPeak * omegaPeak));
      return ((alpha * g * g) / w ** 5) * Math.exp(-1.25 * (omegaPeak / w) ** 4) * Math.pow(gamma, r);
    };
  } else if (model === 'pierson-moskowitz') {
    // Fully developed sea: ω_p from U10 (0.855·g/U — the standard U10 form).
    omegaPeak = (0.855 * g) / V;
    sOmega = (w) => ((ALPHA_PM * g * g) / w ** 5) * Math.exp(-1.25 * (omegaPeak / w) ** 4);
  } else {
    // Phillips (Tessendorf 2001) — sampled directly in k below; ω-form unused.
    omegaPeak = 0;
    sOmega = () => 0;
  }

  // ── Band range in k ───────────────────────────────────────────────────────
  const L = (V * V) / g;
  let kPeak: number;
  if (model === 'phillips') {
    kPeak = 1 / (L * Math.SQRT2);
  } else if (!Number.isFinite(depth)) {
    kPeak = (omegaPeak * omegaPeak) / g;
  } else {
    // Invert ω² = gk·tanh(kD) for k at the spectral peak (few Newton steps).
    let k = (omegaPeak * omegaPeak) / g;
    for (let it = 0; it < 6; it++) {
      const f = g * k * Math.tanh(k * depth) - omegaPeak * omegaPeak;
      const th = Math.tanh(k * depth);
      const df = g * (th + k * depth * (1 - th * th));
      k = Math.max(1e-5, k - f / df);
    }
    kPeak = k;
  }
  const kMin = kPeak / 6;
  const kMax = kPeak * 40;

  // Phillips small-wave cutoff (its own convention).
  const l = L / 1000;
  const phillipsK = (k: number): number =>
    (ALPHA_PM * Math.exp(-1 / (k * L) ** 2) * Math.exp(-(k * k) * (l * l))) / k ** 4;

  const waves: GerstnerWave[] = [];
  const logStep = Math.log(kMax / kMin) / count;
  for (let i = 0; i < count; i++) {
    const k0 = kMin * Math.exp(i * logStep);
    const k1 = kMin * Math.exp((i + 1) * logStep);
    const k = Math.sqrt(k0 * k1); // geometric band center
    // Direction: cosine-squared spread around the wind, sampled
    // deterministically — mostly downwind, a little cross-sea.
    const spread = Math.asin((rand() * 2 - 1) * 0.9) * 0.7;
    const heading = windRad + spread;
    const directional = Math.cos(spread) ** 2;

    const w = omegaOf(k);
    // Band variance: Phillips integrates in k directly; the ω-space families
    // convert with the dispersion Jacobian, then take the TMA depth factor.
    const variance =
      model === 'phillips'
        ? phillipsK(k) * (k1 - k0)
        : sOmega(w) * tma(w) * dOmegaDk(k) * (k1 - k0);
    const A = ampScale * Math.sqrt(Math.max(0, 2 * variance * directional));
    if (A < 1e-6) continue;
    waves.push({
      dirX: Math.cos(heading),
      dirZ: Math.sin(heading),
      amplitude: A,
      wavelength: TAU / k,
      q: steepness / (k * A * count),
      w: k,     // spatial wavenumber
      phi: w,   // ω(k) — physical phase rate
    });
  }
  // Degenerate config (tiny wind + huge cutoff) — never return an empty sea.
  if (waves.length === 0) return createWaveSet({ amplitude: 0.02, wavelength: 2, seed: config.seed });
  return waves;
}
